import { useState, useEffect, useRef, memo } from 'react';
import { motion } from 'framer-motion';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  blurDataURL?: string;
  onLoad?: () => void;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  size?: 'small' | 'medium' | 'large'; // New prop for size optimization
}

// Cache successfully loaded images
const imageCache = new Set<string>();

function getOptimizedSrc(src: string, size?: 'small' | 'medium' | 'large'): string {
  // If the src is already optimized or external URL (like http), return as is
  if (src.startsWith('http') || src.includes('?w=')) {
    return src;
  }
  
  // Add width parameter based on size
  const width = size === 'small' ? 400 : size === 'medium' ? 800 : 1200;
  
  // If using JPG or PNG, we could convert to WebP here for better compression
  // This is a simple example, in production you'd want to use a real image optimization service
  return `${src}?w=${width}`;
}

const OptimizedImage = memo(({
  src,
  alt,
  className = "",
  priority = false,
  objectFit = 'cover',
  blurDataURL,
  onLoad,
  onError,
  size = 'large'
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(imageCache.has(src));
  const [error, setError] = useState(false);
  const placeholderColor = useRef(`hsl(${Math.random() * 360}, 30%, 80%)`);
  const optimizedSrc = getOptimizedSrc(src, size);
  const imgRef = useRef<HTMLImageElement>(null);
  
  // Check if image is in viewport for better lazy loading
  useEffect(() => {
    if (!priority && !isLoaded) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Image is in viewport, start loading it
            const img = new Image();
            img.src = optimizedSrc;
            img.onload = () => {
              setIsLoaded(true);
              imageCache.add(src); // Add to cache once loaded
            };
            img.onerror = () => {
              setError(true);
            };
            // Disconnect after triggering load
            observer.disconnect();
          }
        });
      }, {
        rootMargin: '200px', // Start loading when image is 200px from viewport
        threshold: 0.01
      });
      
      if (imgRef.current) {
        observer.observe(imgRef.current);
      }
      
      return () => observer.disconnect();
    }
  }, [optimizedSrc, priority, isLoaded, src]);
  
  // Preload image if it's marked as priority
  useEffect(() => {
    if (priority && !isLoaded) {
      const img = new Image();
      img.src = optimizedSrc;
      img.onload = () => {
        setIsLoaded(true);
        imageCache.add(src);
      };
      img.onerror = () => {
        setError(true);
      };
    }
  }, [optimizedSrc, priority, isLoaded, src]);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setIsLoaded(true);
    imageCache.add(src);
    onLoad?.();
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Image failed to load:', src);
    setError(true);
    onError?.(e);
    e.currentTarget.src = '/placeholder-image.jpg';
  };

  // Create a blur hash placeholder - simple version with CSS
  const blurPlaceholder = blurDataURL || placeholderColor.current;

  return (
    <motion.div
      className={`relative overflow-hidden ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Placeholder that shows until the image loads */}
      {!isLoaded && (
        <div 
          className="absolute inset-0 backdrop-blur-md"
          style={{ 
            backgroundColor: blurPlaceholder,
            backgroundSize: objectFit
          }}
        >
          <div className="absolute inset-0 bg-pulse animate-pulse opacity-50" />
        </div>
      )}
      
      {/* Main image */}
      <img
        ref={imgRef}
        src={isLoaded || priority ? optimizedSrc : ''} // Only set src when loading or if priority
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
        fetchPriority={priority ? "high" : "auto"}
        className={`w-full h-full ${objectFit ? `object-${objectFit}` : ''} transition-opacity duration-500 ease-in-out`}
        style={{ 
          opacity: isLoaded ? 1 : 0,
        }}
        onLoad={handleLoad}
        onError={handleError}
      />
    </motion.div>
  );
});

OptimizedImage.displayName = 'OptimizedImage';

export default OptimizedImage;
